import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';

const MAIN_HEADER_BG = '#1e3a8a';
const MAIN_HEADER_FG = '#ffffff';
const BORDER_LIGHT = '#e2e8f0';
const CELL_BORDER = `1px solid ${BORDER_LIGHT}`;
const ROW_STRIPE_A = '#ffffff';
const ROW_STRIPE_B = '#f8fafc';
const TOTAL_ACTIVE_COL_BG = '#dbeafe';
const TEXT_PRIMARY = '#0f172a';

function HrRacksheetSummaryByCity(props) {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const response = await HttpService.getRosterSummaryByCity();
      if (response?.data?.success === false) {
        props.setSnackInfo(
          response?.data?.message || 'Failed to load headcount summary by city',
          'error'
        );
        setSummaryData(null);
        return;
      }
      setSummaryData(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error('Error loading roster summary by city:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading headcount summary by city',
        'error'
      );
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = Array.isArray(summaryData?.columns) ? summaryData.columns : [];
  const valueKeys = Array.isArray(summaryData?.valueKeys) ? summaryData.valueKeys : [];
  const dataRows = Array.isArray(summaryData?.rows) ? summaryData.rows : [];
  const grandTotal = summaryData?.grandTotal;

  const dimensionCol =
    columns.find((c) => c.type === 'dimension') || columns[0] || { key: 'city', label: 'City' };
  const measureCols = columns.filter((c) => c.type === 'measure');

  const getRowDimensionLabel = (row) => {
    if (!row) return '-';
    const k = dimensionCol.key;
    if (row[k] !== undefined && row[k] !== null) return row[k];
    if (row.city !== undefined) return row.city;
    return '-';
  };

  const valueAtIndex = (row, idx) => {
    if (!row) return '-';
    if (Array.isArray(row.values) && row.values[idx] !== undefined && row.values[idx] !== null) {
      return row.values[idx];
    }
    const key = valueKeys[idx];
    if (key && row.counts && row.counts[key] !== undefined && row.counts[key] !== null) {
      return row.counts[key];
    }
    return '-';
  };

  const headerCellBase = {
    backgroundColor: MAIN_HEADER_BG,
    color: MAIN_HEADER_FG,
    fontWeight: 700,
    border: CELL_BORDER,
    padding: '12px 10px',
  };

  const renderTable = () => {
    if (!summaryData || measureCols.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No summary data available.
        </Typography>
      );
    }

    const allRows = [...dataRows];
    if (grandTotal && (grandTotal.values || grandTotal.counts)) {
      allRows.push(grandTotal);
    }

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        style={{
          maxWidth: '100%',
          overflowX: 'auto',
          border: CELL_BORDER,
          borderRadius: 0,
        }}
      >
        <Table size="small" stickyHeader style={{ borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  ...headerCellBase,
                  textAlign: 'left',
                  minWidth: 120,
                  position: 'sticky',
                  left: 0,
                  zIndex: 5,
                  boxShadow: '2px 0 4px rgba(15,23,42,0.06)',
                }}
              >
                {dimensionCol.label}
              </TableCell>
              {measureCols.map((col) => (
                <TableCell
                  key={col.key}
                  align="center"
                  style={{
                    ...headerCellBase,
                    minWidth: 88,
                    whiteSpace: 'nowrap',
                    zIndex: 3,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allRows.map((row, idx) => {
              const dimLabel = String(getRowDimensionLabel(row) || '').toLowerCase();
              const isGrand = row.rowType === 'grandTotal' || dimLabel === 'grand total';
              const stripeBg = idx % 2 === 0 ? ROW_STRIPE_A : ROW_STRIPE_B;
              const rowBg = isGrand ? MAIN_HEADER_BG : stripeBg;
              const rowFg = isGrand ? MAIN_HEADER_FG : TEXT_PRIMARY;

              return (
                <TableRow key={`${getRowDimensionLabel(row)}-${idx}`}>
                  <TableCell
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      backgroundColor: rowBg,
                      color: rowFg,
                      fontWeight: 700,
                      border: CELL_BORDER,
                      padding: '10px 12px',
                      textAlign: 'left',
                      boxShadow: isGrand ? undefined : '2px 0 4px rgba(15,23,42,0.04)',
                    }}
                  >
                    {getRowDimensionLabel(row)}
                  </TableCell>
                  {measureCols.map((col) => {
                    const vkIdx = valueKeys.indexOf(col.key);
                    const mIdx = vkIdx >= 0 ? vkIdx : measureCols.indexOf(col);
                    const isTotalActive = col.key === 'totalActive';
                    const cellBg = isGrand
                      ? MAIN_HEADER_BG
                      : isTotalActive
                        ? TOTAL_ACTIVE_COL_BG
                        : rowBg;
                    return (
                      <TableCell
                        key={col.key}
                        align="center"
                        style={{
                          backgroundColor: cellBg,
                          color: rowFg,
                          fontWeight: isGrand ? 700 : 400,
                          border: CELL_BORDER,
                          padding: '10px 8px',
                        }}
                      >
                        {valueAtIndex(row, mIdx)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const pageTitle = summaryData?.title || 'Headcount Summary — by City';

  return (
    <Box p={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mb={2}
      >
        <Typography variant="h5" style={{ fontWeight: 700, color: TEXT_PRIMARY }}>
          {pageTitle}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          disabled={loading}
          onClick={() => loadSummary()}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>
      {renderTable()}
    </Box>
  );
}

export default connect(null, { setSnackInfo })(HrRacksheetSummaryByCity);
